/* ──────────────────────────────────────────────────────────────────
 * scheduleSort.js
 *
 * Catalog of sort options used by the Schedule tab. Lives in its own
 * file so both `ScheduleView` (which applies the comparator when
 * computing visibleRows) and `TasksTab` (which renders the sort
 * popover) can import it without creating a circular dependency
 * between the two.
 *
 * Each entry: `key`, `label`, `cmp(a, b)`. Comparators operate on a
 * single section's task list — section-level grouping happens above
 * this in ScheduleView. The empty-string sentinel `'￿'` is used
 * for date comparisons so tasks with missing dates sort to the end
 * consistently in both ascending and (logical) descending directions.
 * ────────────────────────────────────────────────────────────────── */

export const SCHEDULE_SORT_OPTIONS = [
  {
    key: 'default',
    label: 'Section + Code (default)',
    cmp: (a, b) =>
      (a.task_code || a.code || '').localeCompare(
        b.task_code || b.code || '',
        undefined,
        { numeric: true },
      ),
  },
  {
    key: 'code',
    label: 'Code (A→Z)',
    cmp: (a, b) =>
      (a.task_code || a.code || '').localeCompare(
        b.task_code || b.code || '',
        undefined,
        { numeric: true },
      ),
  },
  {
    key: 'name',
    label: 'Name (A→Z)',
    cmp: (a, b) => (a.task_name || a.name || '').localeCompare(b.task_name || b.name || ''),
  },
  {
    key: 'start_asc',
    label: 'Start date (earliest → latest)',
    cmp: (a, b) => (a.start_date || '￿').localeCompare(b.start_date || '￿'),
  },
  {
    key: 'finish_asc',
    label: 'Finish date (earliest → latest)',
    cmp: (a, b) =>
      (a.finish_date || a.end_date || '￿').localeCompare(
        b.finish_date || b.end_date || '￿',
      ),
  },
  {
    key: 'duration_asc',
    label: 'Duration (short → long)',
    cmp: (a, b) =>
      (parseInt(a.duration_days || a.duration, 10) || 0) -
      (parseInt(b.duration_days || b.duration, 10) || 0),
  },
  {
    key: 'duration_desc',
    label: 'Duration (long → short)',
    cmp: (a, b) =>
      (parseInt(b.duration_days || b.duration, 10) || 0) -
      (parseInt(a.duration_days || a.duration, 10) || 0),
  },
  {
    key: 'pct_asc',
    label: '% Complete (low → high)',
    cmp: (a, b) =>
      (parseFloat(a.percent_complete) || 0) - (parseFloat(b.percent_complete) || 0),
  },
  {
    key: 'pct_desc',
    label: '% Complete (high → low)',
    cmp: (a, b) =>
      (parseFloat(b.percent_complete) || 0) - (parseFloat(a.percent_complete) || 0),
  },
  {
    key: 'type',
    label: 'Type (A→Z)',
    cmp: (a, b) => (a.task_type || '').localeCompare(b.task_type || ''),
  },
];

/**
 * Seven allowed task_type values used by the Filter popover. Mirrors
 * the dropdown in TaskEditModal so the filter shows the same set.
 */
export const SCHEDULE_TASK_TYPES = [
  'Labor',
  'Material',
  'Procurement',
  'Engineering',
  'Inspection',
  'Documents',
  'Milestone',
];

/**
 * Status classifier — uses task.status when set to one of the three
 * canonical values, otherwise derives from percent_complete so the
 * filter matches the visible row even if the server-side row hasn't
 * caught up after an inline edit.
 */
export const classifyTaskStatus = (t) => {
  if (t.status === 'Complete' || t.status === 'In Progress' || t.status === 'Not Started') {
    return t.status;
  }
  const pct = parseFloat(t.percent_complete) || 0;
  if (pct >= 100) return 'Complete';
  if (pct > 0) return 'In Progress';
  return 'Not Started';
};
